import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './ProductSearchBar.css';

// Search dropdown card component
function SearchDropdownCard({ product, onClick }) {
  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onClick(product);
  };

  return (
    <div className="search-dropdown-card" onClick={handleClick}>
      <div className="search-dropdown-card-image">
        <img
          src={product.thumbnail || product.image || 'https://via.placeholder.com/60x60/333/666?text=No+Img'}
          alt={product.name}
          onError={(e) => {
            e.target.src = 'https://via.placeholder.com/60x60/333/666?text=No+Img';
          }}
        />
      </div>
      <div className="search-dropdown-card-content">
        <h4 className="search-dropdown-card-title">{product.name}</h4>
        <div className="search-dropdown-card-meta">
          <span className="search-dropdown-card-price">${product.price}</span>
          {product.tags && product.tags.length > 0 && (
            <span className="search-dropdown-card-tags">
              {product.tags.slice(0, 2).join(', ')}
            </span>
          )}
        </div>
      </div>
      <div className="search-dropdown-card-arrow">
        <i className="fa-solid fa-chevron-right"></i>
      </div>
    </div>
  );
}

export function ProductSearchBar({
  searchTerm,
  onSearchChange,
  onSubmit,
  placeholder = "Search products...",
  context = "main", // "main" or "details"
  searchResults = [] // For details context - array of products to search through
}) {
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [dropdownResults, setDropdownResults] = useState([]);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);


  const handleSearchChange = (e) => {
    const value = e.target.value;
    onSearchChange(value);

    // For details context, debounce the search and show dropdown
    if (context === "details") {
      // Clear existing debounce
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      // If search term is empty, close dropdown immediately
      if (!value.trim()) {
        setIsDropdownOpen(false);
        setDropdownResults([]);
        return;
      }

      // Debounce exists for UI responsiveness, but dropdown visibility is now reactive to searchResults changes
      debounceRef.current = setTimeout(() => {
        // Debounce timeout reached - searchResults will be updated by parent component
        // Dropdown visibility is handled by useEffect that watches searchResults
      }, 250); // 250ms debounce like main products search
    }
  };

  const handleKeyDown = (e) => {
    if (context === "details") {
      if (e.key === 'Enter') {
        e.preventDefault();
        if (searchTerm.trim()) {
          // Navigate to main products page with search
          const params = new URLSearchParams();
          params.set('query', searchTerm.trim());
          navigate(`/marketplace?${params.toString()}`);
          setIsDropdownOpen(false);
        }
      } else if (e.key === 'Escape') {
        setIsDropdownOpen(false);
      }
    } else {
      // Main context behavior
      if (e.key === 'Enter' && onSubmit) {
        e.preventDefault();
        onSubmit(searchTerm);
      }
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (context === "details") {
      if (searchTerm.trim()) {
        const params = new URLSearchParams();
        params.set('query', searchTerm.trim());
        navigate(`/marketplace?${params.toString()}`);
        setIsDropdownOpen(false);
      }
    } else {
      if (onSubmit) {
        onSubmit(searchTerm);
      }
    }
  };

  const handleResultClick = (product) => {
    // Navigate to the clicked product's detail page
    const params = new URLSearchParams();
    params.set('productId', product.id);
    navigate(`/marketplace/details?${params.toString()}`, { replace: false });
    setIsDropdownOpen(false);
  };

  // Update dropdown when search results change (for details context)
  useEffect(() => {
    if (context === "details") {
      const hasQuery = searchTerm.trim().length > 0;
      if (hasQuery && searchResults.length > 0) {
        const results = searchResults.slice(0, 7); // Limit to 7 results
        setDropdownResults(results);
        setIsDropdownOpen(true);
      } else {
        setDropdownResults([]);
        setIsDropdownOpen(false);
      }
    }
  }, [searchResults, context, searchTerm]);

  // Close dropdown when clicking outside and cleanup debounce
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  return (
    <div className="product-search-bar" ref={searchRef}>
      <form className="product-search-form" onSubmit={handleSearchSubmit}>
        <div className="product-search-container">
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="product-search-input"
          />
          <button type="submit" className="product-search-button">
            <i className="fa-solid fa-magnifying-glass"></i>
          </button>
        </div>

        {/* Dropdown for details context */}
        {context === "details" && isDropdownOpen && dropdownResults.length > 0 && (
          <div className="product-search-dropdown">
            {dropdownResults.map((product, index) => (
              <SearchDropdownCard
                key={product.id}
                product={product}
                onClick={() => handleResultClick(product)}
              />
            ))}
          </div>
        )}
      </form>
    </div>
  );
}